import { useState, useEffect, useRef } from "react";

const CatchphraserCommunity = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const intervalRef = useRef(null);
  const slideDuration = 5000;

  const asset = (name) => `${process.env.REACT_APP_USER_API_URL}/uploads/screens/${name}`;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const LinkIcon = () => (
    <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );

  const ExternalLink = ({ compact = false }) => (
    <a
      href="https://catchphraser.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center rounded-full border border-blue-200 bg-white/85 text-blue-700 shadow-sm backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-md ${
        compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      } font-semibold`}
    >
      <LinkIcon />
      catchphraser.com
    </a>
  );

  const DesktopLink = () => (
    <div className="absolute top-4 right-4 z-20 hidden sm:block">
      <ExternalLink />
    </div>
  );

  const MobileLink = () => (
    <div className="mb-3 flex justify-center sm:hidden">
      <ExternalLink compact />
    </div>
  );

  const Pill = ({ children, color = "blue" }) => {
    const colors = {
      blue: "bg-blue-50 text-blue-700 ring-blue-200",
      purple: "bg-purple-50 text-purple-700 ring-purple-200",
      green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      pink: "bg-pink-50 text-pink-700 ring-pink-200",
      gray: "bg-gray-50 text-gray-700 ring-gray-200",
      amber: "bg-amber-50 text-amber-700 ring-amber-200",
      cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    };

    return (
      <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ${colors[color]}`}>
        {children}
      </span>
    );
  };

  const SoftCard = ({ children, className = "" }) => (
    <div className={`rounded-3xl border border-white/70 bg-white/82 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm ${className}`}>
      {children}
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div className="mx-auto mb-4 inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-gray-500 shadow-sm">
      {children}
    </div>
  );

  const ConceptMark = ({ type = "default" }) => {
    const config = {
      plateau: {
        label: "Plateau",
        sub: "B1 → B2",
        from: "from-blue-500",
        to: "to-purple-500",
        graphic: (
          <div className="flex h-14 w-28 items-end gap-1.5">
            {[18, 30, 42, 42, 42, 42].map((height, index) => (
              <div key={index} className="w-full rounded-t-lg bg-white/80" style={{ height }} />
            ))}
          </div>
        ),
      },
      puzzle: {
        label: "Gap",
        sub: "tools ≠ fluency",
        from: "from-violet-500",
        to: "to-fuchsia-500",
        graphic: <div className="h-16 w-16 rounded-[1.4rem] border-[10px] border-white/85" />,
      },
      coordination: {
        label: "Coordination",
        sub: "ear • voice • rhythm",
        from: "from-sky-500",
        to: "to-indigo-500",
        graphic: (
          <div className="flex h-16 items-center gap-1.5">
            {[18, 34, 52, 38, 58, 28, 44].map((height, index) => (
              <div key={index} className="w-2 rounded-full bg-white/85" style={{ height }} />
            ))}
          </div>
        ),
      },
      repetition: {
        label: "Repetition",
        sub: "practice → purpose",
        from: "from-emerald-500",
        to: "to-blue-500",
        graphic: <div className="h-16 w-16 rounded-full border-[10px] border-white/85 border-r-white/30" />,
      },
      community: {
        label: "Community",
        sub: "peer learning",
        from: "from-purple-500",
        to: "to-pink-500",
        graphic: (
          <div className="relative h-16 w-20">
            <div className="absolute left-6 top-0 h-9 w-9 rounded-full bg-white/90" />
            <div className="absolute left-0 top-7 h-8 w-8 rounded-full bg-white/70" />
            <div className="absolute right-0 top-7 h-8 w-8 rounded-full bg-white/70" />
          </div>
        ),
      },
      listening: {
        label: "Prosody",
        sub: "listen • echo • shadow",
        from: "from-cyan-500",
        to: "to-blue-500",
        graphic: (
          <div className="relative h-16 w-24">
            <div className="absolute left-1 top-7 h-2 w-22 rounded-full bg-white/40" />
            <div className="absolute left-2 top-6 h-3 w-3 rounded-full bg-white/90" />
            <div className="absolute left-9 top-3 h-9 w-3 rounded-full bg-white/85" />
            <div className="absolute left-16 top-1 h-13 w-3 rounded-full bg-white/75" />
          </div>
        ),
      },
      ai: {
        label: "AI + Human",
        sub: "prepare → interact",
        from: "from-blue-600",
        to: "to-violet-600",
        graphic: <div className="grid h-16 w-16 grid-cols-2 gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="rounded-xl bg-white/85" />)}</div>,
      },
      review: {
        label: "Rate me",
        sub: "community review",
        from: "from-blue-500",
        to: "to-cyan-500",
        graphic: <div className="h-14 w-20 rounded-2xl bg-white/85 shadow-inner"><div className="mx-auto mt-4 h-2 w-12 rounded-full bg-blue-300" /><div className="mx-auto mt-2 h-2 w-8 rounded-full bg-blue-200" /></div>,
      },
      discussion: {
        label: "Repair",
        sub: "mark • explain • retry",
        from: "from-purple-500",
        to: "to-pink-500",
        graphic: <div className="relative h-16 w-24"><div className="absolute left-0 top-2 h-10 w-16 rounded-2xl bg-white/85" /><div className="absolute right-0 top-8 h-8 w-14 rounded-2xl bg-white/65" /></div>,
      },
      visual: {
        label: "Visualize",
        sub: "pitch • rhythm",
        from: "from-indigo-500",
        to: "to-purple-500",
        graphic: (
          <svg viewBox="0 0 120 60" className="h-16 w-28">
            <path d="M8 42 C 28 10, 45 52, 65 25 S 98 10, 112 34" fill="none" stroke="white" strokeWidth="8" strokeLinecap="round" />
          </svg>
        ),
      },
      rehearsal: {
        label: "Rehearsal",
        sub: "dialogue rhythm",
        from: "from-violet-500",
        to: "to-fuchsia-500",
        graphic: <div className="flex h-16 w-24 items-center justify-between"><div className="h-12 w-9 rounded-full bg-white/85" /><div className="h-1 w-8 rounded-full bg-white/50" /><div className="h-12 w-9 rounded-full bg-white/65" /></div>,
      },
      loop: {
        label: "Loop",
        sub: "feedback cycle",
        from: "from-pink-500",
        to: "to-orange-500",
        graphic: <div className="h-16 w-16 rounded-full border-[10px] border-white/85 border-b-white/25" />,
      },
      course: {
        label: "Course",
        sub: "structured pedagogy",
        from: "from-purple-600",
        to: "to-blue-600",
        graphic: <div className="h-14 w-20 rounded-2xl bg-white/85"><div className="mx-3 mt-3 h-2 rounded bg-purple-200" /><div className="mx-3 mt-2 h-2 rounded bg-purple-200" /><div className="mx-3 mt-2 h-2 w-8 rounded bg-purple-200" /></div>,
      },
      structure: {
        label: "Structure",
        sub: "precise repair",
        from: "from-sky-500",
        to: "to-blue-600",
        graphic: <div className="grid h-16 w-16 grid-cols-3 gap-1.5">{Array.from({ length: 9 }).map((_, i) => <div key={i} className="rounded-md bg-white/80" />)}</div>,
      },
      l1: {
        label: "L1-aware",
        sub: "targeted guidance",
        from: "from-slate-600",
        to: "to-cyan-600",
        graphic: <div className="relative h-16 w-20"><div className="absolute left-1 top-5 h-10 w-10 rounded-full bg-white/75" /><div className="absolute right-1 top-1 h-10 w-10 rounded-full bg-white/90" /></div>,
      },
      future: {
        label: "Next phase",
        sub: "dialogue rehearsal",
        from: "from-blue-600",
        to: "to-purple-600",
        graphic: <div className="flex h-16 w-24 items-end gap-2">{[22, 34, 46, 58].map((h, i) => <div key={i} className="w-full rounded-t-xl bg-white/80" style={{ height: h }} />)}</div>,
      },
      guided: {
        label: "Guided",
        sub: "premium sessions",
        from: "from-purple-600",
        to: "to-pink-600",
        graphic: <div className="h-16 w-16 rounded-2xl bg-white/85"><div className="mx-auto mt-3 h-8 w-8 rounded-full bg-pink-200" /></div>,
      },
      summary: {
        label: "Summary",
        sub: "practice → rehearsal",
        from: "from-emerald-500",
        to: "to-purple-500",
        graphic: <div className="flex h-14 w-24 items-center gap-2"><div className="h-10 w-10 rounded-full bg-white/85" /><div className="h-1 w-8 rounded-full bg-white/60" /><div className="h-10 w-10 rounded-2xl bg-white/70" /></div>,
      },
    };

    const item = config[type] || config.plateau;

    return (
      <div className="mx-auto mb-5 flex flex-col items-center">
        <div className={`flex h-28 w-28 items-center justify-center rounded-[2rem] bg-gradient-to-br ${item.from} ${item.to} shadow-[0_22px_60px_rgba(15,23,42,0.18)] ring-1 ring-white/50`}>
          {item.graphic}
        </div>
        <div className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-gray-500">{item.label}</div>
        <div className="text-xs font-semibold text-gray-400">{item.sub}</div>
      </div>
    );
  };

  const ScreenshotFrame = ({ src, alt, caption, className = "", imgClassName = "" }) => (
    <div className={`rounded-[2rem] border border-gray-200/80 bg-white p-3 shadow-[0_25px_80px_rgba(15,23,42,0.12)] ${className}`}>
      <img src={src} alt={alt} className={`w-full rounded-[1.35rem] border border-gray-100 object-cover ${imgClassName}`} />
      {caption && <div className="px-2 pt-3 text-center text-xs font-medium text-gray-500">{caption}</div>}
    </div>
  );

  const NumberedList = ({ items, accent = "blue" }) => {
    const color = accent === "purple" ? "text-purple-600" : accent === "green" ? "text-emerald-600" : "text-blue-600";
    return (
      <div className="space-y-3 text-left">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-sm font-black shadow-sm ${color}`}>
              {index + 1}
            </div>
            <div className="text-gray-700 leading-relaxed">{item}</div>
          </div>
        ))}
      </div>
    );
  };

  const ComparisonTable = ({ leftTitle, rightTitle, rows, leftColor = "blue", rightColor = "purple" }) => {
    const leftClasses = leftColor === "green" ? "from-emerald-50 to-white ring-emerald-200" : "from-blue-50 to-white ring-blue-200";
    const rightClasses = rightColor === "pink" ? "from-pink-50 to-white ring-pink-200" : "from-purple-50 to-white ring-purple-200";

    return (
      <div className="grid w-full gap-5 md:grid-cols-2">
        <div className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${leftClasses}`}>
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">{leftTitle}</h4>
          <NumberedList items={rows.map((row) => row[0])} accent={leftColor === "green" ? "green" : "blue"} />
        </div>

        <div className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${rightClasses}`}>
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">{rightTitle}</h4>
          <NumberedList items={rows.map((row) => row[1])} accent="purple" />
        </div>
      </div>
    );
  };

  const BigStatement = ({ title, children, tone = "blue" }) => {
    const tones = {
      blue: "from-blue-100 via-white to-purple-100 border-blue-200",
      green: "from-emerald-100 via-white to-blue-100 border-emerald-200",
      purple: "from-purple-100 via-white to-pink-100 border-purple-200",
      amber: "from-amber-100 via-white to-orange-100 border-amber-200",
    };

    return (
      <div className={`rounded-3xl border bg-gradient-to-r p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${tones[tone]}`}>
        <div className="mb-2 text-xl font-black text-gray-950">{title}</div>
        <div className="text-gray-700 leading-relaxed">{children}</div>
      </div>
    );
  };

  const slides = [
    {
      title: "",
      content: (
        <div className="relative overflow-hidden px-2 py-8 text-center sm:py-14">
          <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
            <ExternalLink compact />
          </div>

          <div className="mx-auto mb-8 flex max-w-4xl flex-col items-center">
            <div className="mb-5 inline-flex rounded-full bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] text-gray-500 shadow-sm ring-1 ring-gray-200">
              pronunciation • prosody • rehearsal
            </div>

            <h1 className="font-mono text-4xl font-black leading-[0.95] tracking-tight text-gray-950 sm:text-5xl md:text-7xl">
              The<br />Catchphraser
            </h1>

            <div className="my-7 h-1.5 w-44 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 sm:w-72" />

            <h2 className="max-w-4xl text-2xl font-black leading-tight text-gray-850 sm:text-3xl md:text-5xl">
              From pronunciation practice<br className="hidden sm:block" /> to collaborative rehearsal
            </h2>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Pill color="cyan">live product</Pill>
              <Pill color="blue">AI-supported</Pill>
              <Pill color="purple">peer-driven</Pill>
              <Pill color="green">rehearsal-based</Pill>
            </div>
          </div>

          <div className="mx-auto mt-8 max-w-3xl rounded-3xl border border-blue-100 bg-white/80 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm">
            <div className="mb-3 text-sm font-black uppercase tracking-[0.16em] text-blue-700">
              What the product already does
            </div>

            <div className="grid gap-3 text-left sm:grid-cols-2">
              {[
                "AI-supported pronunciation training",
                "echoing and shadowing exercises",
                "prosody visualization",
                "peer pronunciation review",
                "community discussion threads",
                "structured pronunciation feedback",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-2xl bg-blue-50/70 px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-blue-100">
                  <div className="h-2 w-2 rounded-full bg-emerald-500" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="mx-auto mt-6 max-w-md rounded-2xl border border-gray-200 bg-white/70 px-5 py-3 text-sm font-medium text-gray-500 shadow-sm backdrop-blur-sm sm:text-base">
            Press spacebar or → to continue
          </div>
        </div>
      ),
      bgColor: "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "The Intermediate Plateau",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <ConceptMark type="plateau" />
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Many learners plateau long before fluency</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            They know vocabulary and grammar well enough to communicate, but still struggle with timing, rhythm, confidence, and socially natural speech.
          </p>

          <ComparisonTable
            leftTitle="Learners can often manage..."
            rightTitle="But still struggle with..."
            rows={[
              ["vocabulary", "intonation"],
              ["grammar", "timing"],
              ["information transfer", "emotional nuance"],
              ["correct sentences", "socially natural speech"],
            ]}
          />

          <BigStatement title="Communication is not only semantic.">
            It is rhythmic, emotional, and social.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Why Existing Models Plateau",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🧩</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Existing approaches solve only part of the problem</h3>

          <div className="grid gap-4 md:grid-cols-5">
            {[
              ["📱", "Self-study", "Often socially disconnected"],
              ["🌍", "Immersion", "Inaccessible for many learners"],
              ["👩‍🏫", "Tutoring", "Can create evaluative pressure"],
              ["🤖", "AI chat", "Does not fully train human coordination"],
              ["🔥", "Streak apps", "Can become repetition without progression"],
            ].map(([icon, title, text], index) => (
              <SoftCard key={index} className="p-5 text-center">
                <div className="mb-3 text-4xl">{icon}</div>
                <h4 className="mb-2 font-black text-gray-950">{title}</h4>
                <p className="text-sm leading-relaxed text-gray-600">{text}</p>
              </SoftCard>
            ))}
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <SoftCard className="bg-blue-50/75 text-left ring-1 ring-blue-100">
              <h4 className="mb-3 text-xl font-black text-gray-950">The access problem</h4>
              <p className="leading-relaxed text-gray-700">Many learners simply do not have regular access to supportive speaking communities or patient native speakers.</p>
            </SoftCard>

            <SoftCard className="bg-purple-50/75 text-left ring-1 ring-purple-100">
              <h4 className="mb-3 text-xl font-black text-gray-950">The social anxiety problem</h4>
              <p className="leading-relaxed text-gray-700">AI may reduce embarrassment, but it does not necessarily reduce the fear of speaking with people.</p>
            </SoftCard>
          </div>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Speech Is A Coordination Skill",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🎼</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Pronunciation is not only knowledge</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            Speech involves auditory perception, vocal motor coordination, rhythm, timing, and social responsiveness.
          </p>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              ["👂", "Ear", "perception"],
              ["🗣️", "Voice", "articulation"],
              ["🎵", "Rhythm", "timing"],
              ["👥", "Interaction", "response"],
            ].map(([icon, title, text], index) => (
              <SoftCard key={index} className="p-6 text-center ring-1 ring-blue-100">
                <div className="mb-3 text-5xl">{icon}</div>
                <h4 className="text-xl font-black text-gray-950">{title}</h4>
                <p className="mt-1 text-gray-600">{text}</p>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="Like music, sports, or theater">
            speech requires repeated embodied practice.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Practice vs Rehearsal",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🔁</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">We value repetition — but repetition should lead somewhere</h3>

          <ComparisonTable
            leftTitle="Micro-practice loops"
            rightTitle="Rehearsal-based learning"
            leftColor="green"
            rows={[
              ["isolated exercises", "preparation for interaction"],
              ["streak continuity", "cumulative progression"],
              ["engagement mechanics", "collaborative purpose"],
              ["correctness-focused", "responsiveness-focused"],
              ["task completion", "expressive development"],
            ]}
          />

          <BigStatement title="Repetition is essential for pronunciation." tone="green">
            But it becomes more meaningful when it prepares the learner for coordination, interaction, and performance.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "Why Peer-Driven Learning Matters",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">👥</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Community is part of the learning mechanism</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            Learners do not improve only by hearing perfect speech. They also improve by hearing others struggle, repair, adapt, and gradually become more natural.
          </p>

          <ComparisonTable
            leftTitle="Native models"
            rightTitle="Peer participation"
            rows={[
              ["show the target", "show the path"],
              ["ideal pronunciation", "visible gradual improvement"],
              ["reference", "participation"],
              ["polished delivery", "realistic variability"],
            ]}
          />

          <div className="grid gap-4 md:grid-cols-4">
            {[
              ["🌱", "Normalizes imperfection"],
              ["😌", "Reduces inhibition"],
              ["👂", "Trains perception"],
              ["🤝", "Creates commitment"],
            ].map(([icon, text], index) => (
              <SoftCard key={index} className="p-4 text-center ring-1 ring-purple-100">
                <div className="mb-2 text-3xl">{icon}</div>
                <div className="text-sm font-bold text-gray-850">{text}</div>
              </SoftCard>
            ))}
          </div>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Individual Prosody Training",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🎧</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Echoing and shadowing train speech internally</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            Because the words are already given, learners can focus less on grammar construction and more on pronunciation, rhythm, stress, intonation, and timing.
          </p>

          <ComparisonTable
            leftTitle="Echoing"
            rightTitle="Shadowing"
            rows={[
              ["listen → pause → repeat", "listen → speak in real time"],
              ["articulation precision", "rhythm and flow"],
              ["targeted correction", "speech coordination"],
              ["fine motor control", "temporal synchronization"],
            ]}
          />

          <BigStatement title="Human audio models remain central.">
            They provide rhythm, stress, intonation, and natural speech flow.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#eff6ff,#ecfeff)]",
    },

    {
      title: "AI-Supported Pronunciation Training",
      content: (
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🤖</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">AI as preparation, not replacement</h3>

          <div className="grid items-center gap-8 lg:grid-cols-2">
            <ScreenshotFrame
              src={asset("kattenspiserikkefisk.png")}
              alt="Phoneme accuracy feedback interface"
              caption="AI-supported pronunciation practice with phonetic feedback"
            />

            <div className="space-y-5">
              <ComparisonTable
                leftTitle="AI practice"
                rightTitle="Human rehearsal"
                rows={[
                  ["safe repetition", "social coordination"],
                  ["immediate feedback", "emotional timing"],
                  ["pronunciation correction", "responsiveness"],
                  ["unlimited availability", "collaborative unpredictability"],
                ]}
              />
            </div>
          </div>

          <BigStatement title="AI prepares speech.">
            Human rehearsal activates speech socially.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Community Pronunciation Review",
      content: (
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="mb-8 text-center">
            <SectionLabel>Product Workflow</SectionLabel>
            <div className="text-5xl mb-4">📣</div>
            <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Learners can ask the community to review pronunciation</h3>
            <p className="mx-auto mt-4 max-w-3xl text-xl leading-relaxed text-gray-700">
              The “Rate me” flow turns pronunciation practice into a shared listening task.
            </p>
          </div>

          <div className="grid items-center gap-8 lg:grid-cols-[1.25fr_0.75fr]">
            <ScreenshotFrame
              src={asset("rateme.png")}
              alt="Rate Me pronunciation review interface"
              caption="Learner request for structured pronunciation feedback"
              imgClassName="max-h-[520px] object-contain bg-gray-50"
            />

            <div className="space-y-5">
              <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
                <h4 className="mb-4 text-2xl font-black text-gray-950">What reviewers can do</h4>
                <NumberedList
                  items={[
                    "replay the learner attempt",
                    "compare it with narrator audio",
                    "inspect rhythm and melody",
                    "mark unclear words or sounds",
                    "leave concrete suggestions",
                  ]}
                />
              </SoftCard>

              <BigStatement title="Feedback is not only correction.">
                It also trains listening, attention, and pronunciation awareness.
              </BigStatement>
            </div>
          </div>
        </div>
      ),
      bgColor: "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Collaborative Repair & Discussion",
      content: (
        <div className="mx-auto max-w-6xl space-y-7 px-4 py-8 text-center">
          <div>
            <SectionLabel>Product Workflow</SectionLabel>
            <div className="text-6xl">💬</div>
            <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Feedback becomes part of the learning loop</h3>
            <p className="mx-auto mt-4 max-w-3xl text-xl leading-relaxed text-gray-700">
              Users can mark pronunciation issues, explain them, reply, and return to practice with a clearer target.
            </p>
          </div>

          <ScreenshotFrame
            src={asset("rated.png")}
            alt="Completed pronunciation rating with marked words and comment discussion"
            caption="Completed peer verdict with marked segments and discussion"
            className="mx-auto max-w-5xl"
            imgClassName="max-h-[560px] object-contain bg-gray-50"
          />

          <div className="grid gap-4 md:grid-cols-4">
            {[
              ["🎯", "mark difficult sounds"],
              ["🎵", "notice rhythm issues"],
              ["✍️", "explain correction"],
              ["🔁", "retry with a clearer target"],
            ].map(([icon, text], index) => (
              <SoftCard key={index} className="p-4 text-center">
                <div className="mb-2 text-3xl">{icon}</div>
                <div className="font-bold text-gray-850">{text}</div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="Learners improve not only by speaking." tone="purple">
            They also improve by learning to hear speech more precisely.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[radial-gradient(circle_at_top_right,_#f3e8ff,_transparent_35%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Prosody Visualization",
      content: (
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">📈</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Seeing rhythm, stress, and intonation</h3>
          <p className="mx-auto max-w-3xl text-xl text-gray-600">
            Visual feedback helps learners notice how words form intonation groups and how pitch moves inside a phrase.
          </p>

          <ScreenshotFrame
            src={asset("muchwarmerthere.png")}
            alt="Pitch contour visualization showing intonation groups and pitch variation within words"
            className="mx-auto max-w-5xl"
          />

          <BigStatement title="Technology supports perception." tone="purple">
            The goal is not merely analysis, but socially usable speech.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Social Feedback Layer",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">💬</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Feedback trains both speaker and listener</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            When learners evaluate others, they often notice rhythm, stress, unclear sounds, and intonation patterns before recognizing similar issues in themselves.
          </p>

          <div className="grid items-stretch gap-3 text-center md:grid-cols-5">
            {[
              ["🎧", "Practice"],
              ["⬇️", "Receive feedback"],
              ["💬", "Give feedback"],
              ["👂", "Improve perception"],
              ["🎯", "Refine speech"],
            ].map(([icon, text], index) => (
              <SoftCard key={index} className="p-5">
                <div className="mb-3 text-4xl">{icon}</div>
                <div className="font-black text-gray-950">{text}</div>
              </SoftCard>
            ))}
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
              <div className="mb-3 text-4xl">📣</div>
              <h4 className="mb-2 font-black text-gray-950">Rate me</h4>
              <p className="text-sm text-gray-600">Ask for pronunciation and prosody feedback from others.</p>
            </SoftCard>
            <SoftCard className="bg-purple-50/70 ring-1 ring-purple-100">
              <div className="mb-3 text-4xl">🧠</div>
              <h4 className="mb-2 font-black text-gray-950">Analytical listening</h4>
              <p className="text-sm text-gray-600">Turn community listening into perception training.</p>
            </SoftCard>
            <SoftCard className="bg-emerald-50/70 ring-1 ring-emerald-100">
              <div className="mb-3 text-4xl">🔁</div>
              <h4 className="mb-2 font-black text-gray-950">Return to practice</h4>
              <p className="text-sm text-gray-600">Use feedback to refine the next attempt.</p>
            </SoftCard>
          </div>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "From Practice To Rehearsal",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🎭</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Dialogue turns pronunciation into coordination</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            Dialogue reading and rehearsal train turn-taking, timing, emotional responsiveness, and conversational rhythm.
          </p>

          <ComparisonTable
            leftTitle="Individual training"
            rightTitle="Collaborative rehearsal"
            rows={[
              ["internal rhythm", "shared timing"],
              ["articulation", "interaction"],
              ["pronunciation", "coordination"],
              ["repetition", "responsiveness"],
              ["isolated production", "social adaptation"],
            ]}
          />

          <BigStatement title="Native-speaker interaction remains ideal.">
            But structured peer rehearsal can still provide meaningful communicative growth when native-speaking environments are unavailable.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "The Rehearsal Loop",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🔄</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Practice → Feedback → Rehearsal → Refinement</h3>

          <div className="grid gap-3 md:grid-cols-6">
            {[
              ["🎙️", "Audio models"],
              ["🎧", "Echoing & shadowing"],
              ["🤖", "AI feedback"],
              ["💬", "Peer feedback"],
              ["👥", "Dialogue rehearsal"],
              ["🎭", "Reading groups / plays"],
            ].map(([icon, text], index) => (
              <SoftCard key={index} className="p-4 text-center">
                <div className="mb-3 text-4xl">{icon}</div>
                <div className="text-sm font-black text-gray-950">{text}</div>
              </SoftCard>
            ))}
          </div>

          <SoftCard className="mx-auto max-w-4xl ring-1 ring-purple-100">
            <div className="mb-3 text-xl font-black text-gray-950">The script reduces grammar pressure.</div>
            <p className="leading-relaxed text-gray-700">
              Because the words are already given, learners can focus on delivery: pronunciation, timing, emotion, and response to other actors.
            </p>
          </SoftCard>

          <BigStatement title="Not isolated exercises — a progression toward expressive communication." tone="purple" />
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#fdf2f8)]",
    },

    {
      title: "Structured Courses",
      content: (
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
          <div className="text-center">
            <div className="mb-4 text-6xl">🎓</div>
            <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Pronunciation training with pedagogical structure</h3>
            <p className="mx-auto mt-3 max-w-4xl text-xl text-gray-600">
              Catchphraser is not only a social platform. It is built on structured pronunciation and prosody pedagogy.
            </p>
          </div>

          <div className="grid items-center gap-8 lg:grid-cols-2">
            <SoftCard className="bg-gradient-to-br from-purple-50 to-violet-50 text-center ring-1 ring-purple-100">
              <div className="mx-auto mb-5 h-36 w-36 overflow-hidden rounded-full border-4 border-white shadow-xl">
                <img src={asset("oh.jpg")} alt="Associate Professor Olaf Husby" className="h-full w-full object-cover" />
              </div>
              <h4 className="text-2xl font-black text-gray-950">Assoc. Prof. Olaf Husby</h4>
              <p className="mt-1 font-bold text-purple-700">Course Author – Teaching & Didactics</p>
              <p className="mt-4 leading-relaxed text-gray-700">Phonetician, second language educator, course developer, and Norwegian dialect expert.</p>
            </SoftCard>

            <div className="space-y-5">
              <SoftCard>
                <h4 className="mb-3 text-xl font-black text-gray-950">Flyt og rytme systematically trains</h4>
                <ul className="space-y-3 leading-relaxed text-gray-700">
                  <li>• sentence rhythm and stress alternation</li>
                  <li>• prosodic foot structure and anacrusis control</li>
                  <li>• chaining across word boundaries</li>
                  <li>• reduction and connected speech processes</li>
                  <li>• echoing → shadowing progression toward automaticity</li>
                </ul>
              </SoftCard>

              <BigStatement title="Structured from foundation to mastery">
                Courses can function as preparation for later rehearsal, reading groups, and collaborative performance.
              </BigStatement>
            </div>
          </div>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Clear Structure. Precise Repair.",
      content: (
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
          <div className="text-center">
            <div className="mb-4 text-5xl">🧭</div>
            <h3 className="text-3xl font-black text-gray-950">Structured prosody training inside every exercise</h3>
          </div>

          <div className="grid items-start gap-8 lg:grid-cols-2">
            <ScreenshotFrame
              src={asset("flyt-og-rytme-help.png")}
              alt="Exercise with rhythm pattern, chaining markers and repair instructions"
            />

            <div className="space-y-5">
              <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
                <h4 className="mb-3 text-lg font-black text-gray-950">Clear exercise structure</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• explicit rhythm patterns</li>
                  <li>• visual chaining markers</li>
                  <li>• pickup vs. foot distinction</li>
                  <li>• strong vs. weak syllable control</li>
                  <li>• stable intonation contour</li>
                </ul>
              </SoftCard>

              <SoftCard className="bg-purple-50/70 ring-1 ring-purple-100">
                <h4 className="mb-3 text-lg font-black text-gray-950">Specific repair strategies</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• exact steps when rhythm collapses</li>
                  <li>• compression drills for long anacruses</li>
                  <li>• timing ladder: slow → natural → fast</li>
                  <li>• preparation for dialogue delivery</li>
                </ul>
              </SoftCard>
            </div>
          </div>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "L1-Sensitive Sound Guidance",
      content: (
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-8 px-4 py-8 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="text-5xl">🗺️</div>
            <h3 className="text-3xl font-black text-gray-950">Sound recommendations depend on the learner’s L1</h3>
            <p className="text-lg leading-relaxed text-gray-700">
              Learners with different L1 backgrounds struggle with different sounds. L1-sensitive guidance can prioritize difficult contrasts and provide concrete pronunciation recommendations.
            </p>

            <ul className="space-y-3 text-gray-700">
              <li>• L1-based difficulty profiling</li>
              <li>• targeted guidance for each sound</li>
              <li>• articulation animation</li>
              <li>• examples and closest L1 analogies</li>
            </ul>

            <a href="https://www.catchphraser.com/sounds" target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-full bg-blue-600 px-5 py-2.5 font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-700">
              Open prototype →
            </a>
          </div>

          <ScreenshotFrame
            src={asset("path-to-articulation-image.png")}
            alt="Sound guidance prototype preview"
            caption="Interactive sound map + articulatory view"
            imgClassName="max-h-80 object-cover"
          />
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Next Step: Rehearsal Communities",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🎙️</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Toward reading groups, radio plays, and staged dialogue</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            The product already supports pronunciation training, peer review, community correction, and prosody practice. The next planned layer is dialogue rehearsal: reading groups, collaborative radio plays, staged readings, and role-based speaking activities.
          </p>

          <div className="grid gap-4 md:grid-cols-4">
            {[
              ["📖", "Reading groups", "low-pressure joint practice"],
              ["🎭", "Dialogue rehearsal", "timing and response"],
              ["🎙️", "Radio plays", "shared performance"],
              ["👥", "Rehearsal circles", "community continuity"],
            ].map(([icon, title, text], index) => (
              <SoftCard key={index} className="p-5 text-center">
                <div className="mb-3 text-4xl">{icon}</div>
                <h4 className="mb-2 font-black text-gray-950">{title}</h4>
                <p className="text-sm text-gray-600">{text}</p>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="Current product → planned rehearsal layer">
            The only major element presented here as future development is the play/rehearsal layer. The rest describes the current product.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Guided Activities & Future Monetization",
      content: (
        <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🏋️‍♀️</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">Supervised rehearsal with speech “gym trainers”</h3>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-700">
            For the planned rehearsal layer, advanced groups may include theater students, actors, language volunteers, or native-speaking guides.
          </p>

          <div className="grid gap-5 md:grid-cols-3">
            <SoftCard className="ring-1 ring-blue-100">
              <div className="mb-3 text-5xl">🎭</div>
              <h4 className="mb-2 text-xl font-black text-gray-950">Theater students</h4>
              <p className="text-gray-600">Voice, timing, emotional delivery, and role work.</p>
            </SoftCard>

            <SoftCard className="ring-1 ring-purple-100">
              <div className="mb-3 text-5xl">🗣️</div>
              <h4 className="mb-2 text-xl font-black text-gray-950">Native-speaking guides</h4>
              <p className="text-gray-600">Natural prosody, pragmatic nuance, and live interactional rhythm.</p>
            </SoftCard>

            <SoftCard className="ring-1 ring-emerald-100">
              <div className="mb-3 text-5xl">💼</div>
              <h4 className="mb-2 text-xl font-black text-gray-950">Premium sessions</h4>
              <p className="text-gray-600">Guided rooms, workshops, university partnerships, and supervised activities.</p>
            </SoftCard>
          </div>

          <BigStatement title="Not replacing peer learning — enriching it." tone="purple">
            Like gym trainers, guided facilitators can help coordinate practice without turning the platform into a traditional tutoring marketplace.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Summary",
      content: (
        <div className="mx-auto max-w-4xl space-y-8 px-4 py-8 text-center">
          <div className="text-6xl">🎯</div>
          <h3 className="text-3xl font-black text-gray-950 md:text-4xl">From isolated practice to collaborative rehearsal</h3>

          <div className="grid gap-6 md:grid-cols-2">
            <SoftCard className="bg-gradient-to-br from-emerald-50 to-white ring-1 ring-emerald-100">
              <div className="mb-3 text-5xl">💪</div>
              <h4 className="mb-2 text-xl font-black text-gray-950">Train it like a sport</h4>
              <p className="text-gray-600">Muscle memory, rhythm, repetition, and technical coordination.</p>
            </SoftCard>

            <SoftCard className="bg-gradient-to-br from-purple-50 to-white ring-1 ring-purple-100">
              <div className="mb-3 text-5xl">🎭</div>
              <h4 className="mb-2 text-xl font-black text-gray-950">Rehearse it like theater</h4>
              <p className="text-gray-600">Timing, responsiveness, emotion, and shared performance.</p>
            </SoftCard>
          </div>

          <div className="rounded-3xl border border-blue-200 bg-gradient-to-r from-blue-100 via-white to-purple-100 p-7 shadow-xl">
            <div className="mb-3 text-2xl font-black text-gray-950">Most language platforms optimize isolated practice.</div>
            <div className="text-xl text-gray-700">We want to optimize collaborative rehearsal.</div>
          </div>

          <SoftCard className="border-blue-200 ring-1 ring-blue-100">
            <div className="text-2xl font-black text-gray-950">Not only correct speech — but socially alive speech.</div>
          </SoftCard>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#eff6ff,#faf5ff)]",
    },

    {
      title: "",
      content: (
        <div className="mx-auto max-w-6xl space-y-10 px-4 py-12 text-center">
          <div className="mb-10 text-center">
            <h2 className="mb-5 text-4xl font-black text-gray-950 md:text-5xl">The Catchphraser Team</h2>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600 md:text-2xl">
              Researchers and language professionals building pronunciation training and rehearsal infrastructure.
            </p>
          </div>

          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              [
                "it.jpg",
                "Dr. Inna Tolskaya",
                "CEO – Product & Linguistics",
                "itolsk@gmail.com",
                "Linguist, project manager, and software developer with a passion for innovation and e-learning.",
                "blue",
                "IT",
              ],
              [
                "oh.jpg",
                "Assoc. Prof. Olaf Husby",
                "CCO – Teaching & Didactics",
                "olafhusby@gmail.com",
                "Phonetician, second language educator, course developer, and Norwegian dialects evangelist.",
                "purple",
                "OH",
              ],
              [
                "ds.jpg",
                "Dr. Dmitry Shcherbin",
                "CTO/CPO – Tech & Research",
                "dmitry.shcherbin@gmail.com",
                "Product leader, software developer, data analyst, and researcher with an interest in practical applications.",
                "cyan",
                "DS",
              ],
            ].map(([img, name, role, email, bio, color, initials]) => {
              const cardColor =
                color === "blue"
                  ? "from-blue-50 to-indigo-50 border-blue-200 text-blue-600"
                  : color === "purple"
                  ? "from-purple-50 to-violet-50 border-purple-200 text-purple-600"
                  : "from-cyan-50 to-teal-50 border-cyan-200 text-cyan-600";

              return (
                <div key={name} className={`rounded-3xl border bg-gradient-to-br ${cardColor} p-6 shadow-[0_20px_70px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}>
                  <div className="text-center">
                    <div className="relative mx-auto mb-5 flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-xl">
                      <img
                        src={asset(img)}
                        alt={name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <div className="absolute inset-0 -z-10 flex items-center justify-center text-3xl font-black text-gray-500">{initials}</div>
                    </div>
                    <h3 className="mb-2 text-xl font-black text-gray-950">{name}</h3>
                    <p className="mb-3 text-base font-bold">{role}</p>
                    <a href={`mailto:${email}`} className="break-all text-sm hover:underline">
                      {email}
                    </a>
                  </div>
                  <div className="mt-6 rounded-2xl border border-white/60 bg-white/82 p-5 shadow-sm backdrop-blur-sm">
                    <p className="text-base leading-relaxed text-gray-700">{bio}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <p className="mb-2 text-lg text-gray-700">Interested in collaboration, testing, or research partnerships?</p>
            <a href="mailto:info@catchphraser.com" className="text-lg font-bold text-blue-600 hover:underline">
              info@catchphraser.com
            </a>
          </div>
        </div>
      ),
      bgColor: "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ecfeff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },
  ];

  const resetAutoplayInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setCurrentSlide((current) => {
        if (current < slides.length - 1) return current + 1;
        clearInterval(intervalRef.current);
        setIsPlaying(false);
        return current;
      });
    }, slideDuration);
  };

  const playSlideshow = () => {
    if (isPlaying) {
      clearInterval(intervalRef.current);
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      resetAutoplayInterval();
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide((current) => current + 1);
      if (isPlaying) resetAutoplayInterval();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide((current) => current - 1);
      if (isPlaying) resetAutoplayInterval();
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (isPlaying) resetAutoplayInterval();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          if (currentSlide > 0) prevSlide();
          break;
        case "ArrowRight":
        case " ":
          event.preventDefault();
          if (currentSlide < slides.length - 1) nextSlide();
          break;
        case "p":
        case "P":
          event.preventDefault();
          playSlideshow();
          break;
        case "Home":
          event.preventDefault();
          goToSlide(0);
          break;
        case "End":
          event.preventDefault();
          goToSlide(slides.length - 1);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSlide, isPlaying]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const progress = ((currentSlide + 1) / slides.length) * 100;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_transparent_28%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#f8fafc,#f1f5f9)] p-3 text-gray-900 sm:p-4 md:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl">
        {isMobile && currentSlide > 0 && <MobileLink />}

        <div className="mb-3 flex flex-col items-start justify-between gap-2 sm:mb-4 sm:flex-row sm:items-center">
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold text-gray-500 shadow-sm backdrop-blur-sm sm:text-sm">
            {currentSlide + 1} / {slides.length}
          </div>

          <div className="flex w-full items-center gap-3 sm:w-auto sm:gap-4">
            {isPlaying && (
              <div className="flex items-center rounded-full border border-emerald-200 bg-white/80 px-3 py-1 text-emerald-700 shadow-sm">
                <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                <span className="text-xs font-bold sm:text-sm">Playing</span>
              </div>
            )}
            <button
              onClick={playSlideshow}
              className={`flex flex-1 items-center justify-center rounded-full px-4 py-2 text-xs font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl sm:flex-none sm:text-sm ${
                isPlaying ? "bg-gradient-to-r from-red-500 to-orange-500" : "bg-gradient-to-r from-blue-600 to-purple-600"
              }`}
            >
              {isPlaying ? "⏸️ Pause" : "▶️ Play"}
            </button>
          </div>
        </div>

        <div className="mb-4 h-2 overflow-hidden rounded-full bg-white/70 shadow-inner ring-1 ring-gray-200 sm:mb-6">
          <div className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div className={`relative overflow-hidden rounded-[1.75rem] shadow-[0_30px_100px_rgba(15,23,42,0.14)] ring-1 ring-black/5 transition-all duration-300 sm:rounded-[2rem] ${slides[currentSlide].bgColor}`}>
          {!isMobile && currentSlide > 0 && <DesktopLink />}

          <div className="flex min-h-[60vh] flex-col p-4 sm:min-h-[70vh] sm:p-6 md:p-8 lg:p-12">
            {slides[currentSlide].title && (
              <div className="mb-5 text-center sm:mb-7">
                <h2 className="font-mono text-lg font-black tracking-tight text-gray-950 sm:text-xl md:text-2xl lg:text-3xl">
                  {slides[currentSlide].title}
                </h2>
                <div className="mx-auto mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 sm:w-28" />
              </div>
            )}

            <div className="flex flex-grow items-center overflow-y-auto">
              <div className="w-full">{slides[currentSlide].content}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 sm:mt-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${
              currentSlide === 0 ? "cursor-not-allowed text-gray-400" : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            ← Previous
          </button>

          <div className="flex max-w-[55%] flex-wrap justify-center gap-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 rounded-full transition-all ${
                  currentSlide === index
                    ? isPlaying
                      ? "w-6 animate-pulse bg-emerald-500"
                      : "w-6 bg-blue-600"
                    : "w-2 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${
              currentSlide === slides.length - 1 ? "cursor-not-allowed text-gray-400" : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            {currentSlide === slides.length - 1 ? "✓ Finish" : "Next →"}
          </button>
        </div>

        <div className="mt-3 text-center text-xs font-medium text-gray-400 sm:mt-4">
          ← → to navigate • Space to advance • P to play/pause
        </div>
      </div>
    </div>
  );
};

export default CatchphraserCommunity;
